import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import * as React from 'react';
import { Video } from 'components/Video';
import { StreamState } from 'utils/ui/media';
import { classNames } from 'utils/ui/css';
import Button from 'components/Button';
import { myCameraStream } from 'state/video';

export interface VideoStreamProps {
  // Stream control props
  autoStart?: boolean;
  // Styling props
  containerClassName?: string;
  controlsClassName?: string;
  buttonClassName?: string;
  videoClassName?: string;

  // Event handlers
  onStreamStart?: (stream: MediaStream) => void;
  onStreamError?: (error: Error) => void;
  onStreamStop?: () => void;

  actionElement?: (streamState: StreamState) => React.ReactNode;
}

export const VideoStream: React.FC<VideoStreamProps> = ({
  autoStart = true,
  containerClassName,
  controlsClassName,
  videoClassName,
  onStreamStart,
  onStreamError,
  actionElement,
}) => {
  const {
    isStreaming,
    isAudioEnabled,
    isVideoEnabled,
    error: streamError,
    initialized: streamInitialized,
    isAudioAllowed,
    isVideoAllowed,
  } = myCameraStream().useState() as StreamState;

  const initializeStream = React.useCallback(async (): Promise<void> => {
    try {
      if (!myCameraStream().stream) {
        await myCameraStream().init({
          video: true,
          audio: true,
        });

        const { permissions } = myCameraStream();

        myCameraStream().setValues({
          initialized: true,
          isStreaming: true,
          error: undefined,
          isVideoAllowed: !!permissions?.video,
          isAudioAllowed: !!permissions?.audio,
          isVideoEnabled: true,
          isAudioEnabled: true,
        });

        if (myCameraStream().stream) {
          onStreamStart?.(myCameraStream().stream!);
        }
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to initialize stream');
      myCameraStream().setValues({ error: error.message });
      onStreamError?.(error);
    }
  }, [onStreamStart, onStreamError]);

  React.useEffect(() => {
    /** To avoid calling this at wrong stages of the user experiences (ended session page),
     * we check if the stream is already initialized before calling it.
     */
    if (autoStart && !streamInitialized) {
      initializeStream();
    }
  }, [autoStart, initializeStream, streamInitialized]);

  if (streamError) {
    return (
      <div className={containerClassName}>
        <p>{streamError}</p>
      </div>
    );
  }

  return (
    <div className={classNames(containerClassName, 'relative', 'overflow-auto')} part="voip-waiting-room">
      <Video
        audioMuted={!isAudioEnabled}
        stream={isStreaming && myCameraStream().get('isVideoEnabled') ? myCameraStream().stream : null}
        videoClassName={classNames('size-full object-cover', videoClassName)}
      />

      <div className={controlsClassName} part="voip-waiting-room-controls">
        {streamInitialized && myCameraStream().stream instanceof MediaStream && isAudioAllowed ? (
          <Button
            icon="ex3d-icon--mic"
            onClick={() => myCameraStream().toggleAudio()}
            aria-label={isAudioEnabled ? t`Disable Microphone` : t`Enable Microphone`}
            className={classNames(
              'ex3d-button--secondary',
              'voip-waiting-room-buttons',
              !isAudioEnabled && 'ex3d-disabled'
            )}
          />
        ) : (
          <div className="hidden">
            <Trans>Audio not allowed</Trans>
          </div>
        )}

        {streamInitialized && myCameraStream().stream instanceof MediaStream && isVideoAllowed ? (
          <Button
            icon="ex3d-icon--camera"
            onClick={() => myCameraStream().toggleVideo()}
            aria-label={isVideoEnabled ? t`Disable Camera` : t`Enable Camera`}
            className={classNames(
              'ex3d-button--secondary',
              'voip-waiting-room-buttons',
              !isVideoEnabled && 'ex3d-disabled'
            )}
          />
        ) : (
          <div className="hidden">
            <Trans>Video not allowed</Trans>
          </div>
        )}

        <div className="h-9 flex-1"></div>
        {actionElement?.(myCameraStream().get() as StreamState)}
      </div>
    </div>
  );
};

export default VideoStream;
